import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import {
  EventTypeBackend,
  SortByType,
  TransactionSignatureFields,
} from '../../../../../types/events.types';
import { safeCapTypeTitle, Safe as SafeType } from '../../../../../types/safes.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { formatDisplayValue } from '../../../../../utils/format-display-value.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { EmptyEventHistory } from '../../../empty-event-history';
import { FilesModal } from '../../../modals/index';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { EventSignatureStatusTableCell, Td } from './table-td';

export const Safe: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);
  const { items, totalPages, isLoading } = EventHooks.useTransactions<
    SafeType & TransactionSignatureFields
  >({
    eventId: props.eventId,
    eventType: EventTypeBackend.SAFE,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  if (props.type !== EventTypeBackend.SAFE) return <></>;

  if (props.transactionView && !isLoading && items.length === 0) {
    return (
      <div className="flex w-full justify-center">
        <EmptyEventHistory isDisplayedAddEvent={false} type={SortByType.SAFE} />
      </div>
    );
  }

  const { name } = props;
  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };
  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.SAFE}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {items.map(
          (
            {
              capValue,
              capType,
              floorValue,
              discount,
              issueDate,
              maturityDate,
              stakeholder,
              investment,
              filesLinks,
              docusignFileLinks,
              linkedSignatureFileLink,
              linkedSignatureId,
              signatureStatus,
              signatureSignedCount,
              signatureTotalCount,
            },
            index,
          ) => (
            <tr key={`${index}_${stakeholder}`}>
              <Td value={format(issueDate, 'dd/MM/yyyy')} />
              <Td
                value={<Tooltip content={stakeholder.fullName}>{stakeholder.fullName}</Tooltip>}
              />
              <Td value={formatDisplayValue(investment)} />
              <Td value={formatDisplayValue(capValue)} />
              <Td
                value={
                  capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-'
                }
              />
              <Td value={formatDisplayValue(floorValue)} />
              <Td value={formatDisplayValue(discount, { multiply: 100 })} />
              <Td value={maturityDate ? format(maturityDate, 'dd/MM/yyyy') : '-'} />
              <EventSignatureStatusTableCell
                signatureSignedCount={signatureSignedCount}
                signatureStatus={signatureStatus}
                signatureTotalCount={signatureTotalCount}
              />
              {!props.transactionView && (
                <Td
                  value={
                    <EventTransactionFilesCell
                      date={issueDate}
                      docusignFileLinks={docusignFileLinks}
                      filesLinks={filesLinks}
                      linkedSignatureFileLink={linkedSignatureFileLink}
                      linkedSignatureId={linkedSignatureId}
                      onDocumentClick={handleDocumentClick}
                      onOpenFilesModal={handleOpenFilesModalForRow}
                      signatureStatus={signatureStatus}
                      stakeholderName={stakeholder.fullName}
                    />
                  }
                />
              )}
            </tr>
          ),
        )}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
